import { TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { UserPlus, Users, X } from 'lucide-react';
import type { ChatRoom } from '@/lib/stores/lobbyStore.types';
import { RoomTypeEnum } from '@danda/shared';
import { websocketService } from '@/lib/websocket-service';
import { getSpecialNpcEmoji } from '@danda/shared/utils/avatar-emoji';
import { getStaffAvatarUrl } from '@/lib/media';
import { useLobbyStore } from '@/lib/stores/lobbyStore';
import { useShallow } from 'zustand/react/shallow';

interface ChatRoomTabsProps {
  rooms: ChatRoom[];
  onCloseRoom: (roomId: string) => void;
  children?: React.ReactNode;
}

// Get avatar URL for special NPCs
function getSpecialNpcAvatarUrl(room: ChatRoom): string | null {
  if (room.type === RoomTypeEnum.KIRA || room.name.includes('Kira')) {
    return getStaffAvatarUrl('kira-avatar.webp');
  }
  if (room.type === RoomTypeEnum.ALDRIC || room.name.includes('Aldric')) {
    return getStaffAvatarUrl('aldric-avatar.webp');
  }
  if (room.type === RoomTypeEnum.GROLT || room.name.includes('Grolt')) {
    return getStaffAvatarUrl('grolt-avatar.webp');
  }
  if (room.id === 'main' || room.type === RoomTypeEnum.TAVIAN) {
    return getStaffAvatarUrl('tavian-avatar.webp');
  }
  return null;
}

// Get emoji for room based on type or room data (fallback if no avatar)
function getRoomEmoji(room: ChatRoom): string | null {
  // Use room's avatarEmoji if available
  if (room.avatarEmoji) {
    return room.avatarEmoji;
  }
  // Special NPC emojis - check specific types FIRST before 'main' fallback
  // This ensures Kira/Aldric show their emoji even in the main chat room
  if (room.type === RoomTypeEnum.KIRA || room.name.includes('Kira')) {
    return getSpecialNpcEmoji('kira') ?? '⚔️';
  }
  if (room.type === RoomTypeEnum.ALDRIC || room.name.includes('Aldric')) {
    return getSpecialNpcEmoji('aldric') ?? '🌟';
  }
  if (room.type === RoomTypeEnum.GROLT || room.name.includes('Grolt')) {
    return getSpecialNpcEmoji('grolt') ?? '⚒️';
  }
  if (room.id === 'main' || room.type === RoomTypeEnum.TAVIAN) {
    return getSpecialNpcEmoji('tavian') ?? '🧙‍♂️';
  }
  return null;
}

export function ChatRoomTabs({ rooms, onCloseRoom, children }: ChatRoomTabsProps) {
  const { selectedCharacterId, lobbyCharacters, friends } = useLobbyStore(
    useShallow((s) => ({
      selectedCharacterId: s.selectedCharacterId,
      lobbyCharacters: s.lobbyCharacters,
      friends: s.friends,
    }))
  );

  // A char-chat tab gets an "Add friend" affordance unless the other
  // participant is a known NPC (NPCs have no account to befriend) or their
  // owner is already a friend. PCs in a party are absent from
  // lobbyCharacters, so an unknown partner still shows the button; the
  // server rejects NPC targets as the backstop.
  const charChatCanAddFriend = (room: ChatRoom): boolean => {
    if (!room.id.startsWith('char-chat-')) return false;
    const partnerId = room.participants.find((id) => id !== selectedCharacterId);
    if (!partnerId) return false;
    if (room.partnerUserId && friends.some((f) => f.userId === room.partnerUserId)) {
      return false;
    }
    const known = lobbyCharacters.find((c) => c.id === partnerId);
    return !known || known.type === 'pc';
  };

  return (
    <TabsList className="w-full h-auto mb-2 flex flex-wrap justify-start gap-1 rounded-t-md rounded-b-none">
      {rooms.map((room) => {
        const emoji = getRoomEmoji(room);
        const isParty = room.type === RoomTypeEnum.PARTY;
        const isPrivate = room.type === 'private';
        // Use special NPC avatar URL, then room's avatar, then fallback to emoji
        const specialNpcAvatar = getSpecialNpcAvatarUrl(room);
        const isSpecialNpc = !!specialNpcAvatar;
        const avatarSrc = specialNpcAvatar || room.avatarUrl || room.avatar;
        return (
          <TabsTrigger
            key={room.id}
            value={room.id}
            className="flex items-center gap-1 relative shrink-0"
          >
            {isParty ? (
              <Users className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
            ) : avatarSrc ? (
              <Avatar className={`h-5 w-5 ${isSpecialNpc ? 'ring-2 ring-yellow-500' : ''}`}>
                <AvatarImage src={avatarSrc} alt={room.name} />
                <AvatarFallback className="text-[11px]">{emoji ?? '🙂'}</AvatarFallback>
              </Avatar>
            ) : isPrivate && emoji ? (
              <Avatar className="h-5 w-5">
                <AvatarFallback className="text-[11px]">{emoji}</AvatarFallback>
              </Avatar>
            ) : emoji ? (
              <span className="text-sm">{emoji}</span>
            ) : null}
            <span className="font-character">
              {room.name}
              {isParty && room.memberCount ? ` (${room.memberCount})` : ''}
            </span>
            {room.unreadCount > 0 && (
              <Badge
                variant="destructive"
                className="h-4 w-4 rounded-full p-0 text-[10px] flex items-center justify-center"
              >
                {room.unreadCount > 9 ? '9+' : room.unreadCount}
              </Badge>
            )}
            {charChatCanAddFriend(room) && (
              <span
                role="button"
                tabIndex={0}
                aria-label={`Add ${room.name} as a friend`}
                title="Add friend"
                className="h-4 w-4 p-0 ml-1 inline-flex items-center justify-center rounded hover:bg-primary hover:text-primary-foreground cursor-pointer"
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  websocketService.addFriendFromCharChat(room.id.replace('char-chat-', ''));
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.stopPropagation();
                    e.preventDefault();
                    websocketService.addFriendFromCharChat(room.id.replace('char-chat-', ''));
                  }
                }}
              >
                <UserPlus className="h-3 w-3" />
              </span>
            )}
            {room.id !== 'main' &&
              room.type !== RoomTypeEnum.PARTY &&
              room.type !== RoomTypeEnum.INNER && (
                <span
                  role="button"
                  tabIndex={0}
                  aria-label={`Close ${room.name} tab`}
                  className="h-4 w-4 p-0 ml-1 inline-flex items-center justify-center rounded hover:bg-destructive hover:text-destructive-foreground cursor-pointer"
                  onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    if (room.id.startsWith('char-chat-')) {
                      const chatSessionId = room.id.replace('char-chat-', '');
                      websocketService.closeCharacterChat(chatSessionId);
                    } else if (room.id.startsWith('friend-dm-')) {
                      websocketService.closeFriendDm(room.id.replace('friend-dm-', ''));
                    }
                    onCloseRoom(room.id);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.stopPropagation();
                      e.preventDefault();
                      if (room.id.startsWith('char-chat-')) {
                        const chatSessionId = room.id.replace('char-chat-', '');
                        websocketService.closeCharacterChat(chatSessionId);
                      } else if (room.id.startsWith('friend-dm-')) {
                        websocketService.closeFriendDm(room.id.replace('friend-dm-', ''));
                      }
                      onCloseRoom(room.id);
                    }
                  }}
                >
                  <X className="h-3 w-3" />
                </span>
              )}
          </TabsTrigger>
        );
      })}
      {children}
    </TabsList>
  );
}
